import React, { useEffect, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Divider,
  Chip
} from '@mui/material';
import { AccountCircle, ExitToApp, Cloud, Assignment } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { penholderApiService } from '../../services/PenholderApiService';
import { fetchAuthSession } from 'aws-amplify/auth';

interface MainLayoutProps {
  children: React.ReactNode;
}

const MainLayout: React.FC<MainLayoutProps> = ({ children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [currentPenholderUserId, setCurrentPenholderUserId] = useState<string | null>(null);
  const [penholderLoading, setPenholderLoading] = useState(true);

  // Load current penholder assignment on mount
  useEffect(() => {
    const loadCurrentPenholder = async () => {
      try {
        setPenholderLoading(true);
        console.log('🔍 Loading current penholder assignment...');
        
        // Get authentication session and set token in API service
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString(); // Use ID token instead of access token
        
        console.log('🔑 ID token available:', !!idToken);
        console.log('🔑 ID token length:', idToken?.length || 0);
        console.log('👤 Current user:', user);
        
        if (idToken) {
          penholderApiService.setAuthToken(idToken);
          console.log('✅ ID token set in API service');
        } else {
          console.log('❌ No ID token available');
        }
        
        const assignment = await penholderApiService.getCurrentAssignment();
        console.log('📋 Penholder assignment response:', assignment);
        
        setCurrentPenholderUserId(assignment?.penholderUserId || null);
        console.log('🎯 Current penholder user ID set to:', assignment?.penholderUserId || null);
      } catch (error) {
        console.error('❌ Failed to load current penholder assignment:', error);
        setCurrentPenholderUserId(null);
      } finally {
        setPenholderLoading(false);
      }
    };

    // Only load if user is authenticated
    if (user) {
      console.log('👤 User authenticated, loading penholder assignment...');
      loadCurrentPenholder();
    } else {
      console.log('❌ No user authenticated, skipping penholder assignment load');
    }
  }, [user]);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSignOut = async () => {
    handleClose();
    await signOut();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const isCurrentPath = (path: string) => {
    return location.pathname === path;
  };

  // Check if current user is the assigned penholder
  const isCurrentPenholder = () => {
    console.log('🔍 Checking if current user is penholder...');
    console.log('📊 Penholder loading:', penholderLoading);
    console.log('👤 User:', user);
    console.log('🎯 Current penholder user ID:', currentPenholderUserId);
    
    if (penholderLoading || !user || !currentPenholderUserId) {
      console.log('❌ Not penholder - missing data:', { penholderLoading, hasUser: !!user, hasCurrentPenholderUserId: !!currentPenholderUserId });
      return false;
    }
    
    // Compare user's Cognito sub (UUID) with current penholder assignment
    const isPenholder = user.sub === currentPenholderUserId;
    console.log('🔍 Penholder comparison:', { userSub: user.sub, currentPenholderUserId, isPenholder });
    
    return isPenholder;
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" elevation={1}>
        <Box sx={{ 
          maxWidth: '1400px', 
          mx: 'auto', 
          width: '100%',
          px: 4 
        }}>
          <Toolbar sx={{ px: 0, minHeight: '64px' }}>
            {/* AWS-style logo area */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
              <Cloud sx={{ mr: 1, color: '#FF9900' }} />
              <Typography 
                variant="h6" 
                component="div" 
                sx={{ 
                  fontWeight: 600,
                  color: '#FFFFFF',
                  letterSpacing: '0.5px'
                }}
              >
                Monthly Report Generator
              </Typography>
            </Box>
            
            {/* Navigation area */}
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button 
                color="inherit" 
                onClick={() => handleNavigation('/')}
                sx={{ 
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  backgroundColor: isCurrentPath('/') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  }
                }}
              >
                My Submissions
              </Button>
              
              {/* Penholder Dashboard - show if user is current penholder */}
              {isCurrentPenholder() && (
                <Button 
                  color="inherit" 
                  onClick={() => handleNavigation('/penholder')}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: isCurrentPath('/penholder') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <Assignment sx={{ mr: 1, fontSize: '1.1rem' }} />
                  Penholder
                </Button>
              )}
              
              {user?.isAdmin && (
                <Button 
                  color="inherit" 
                  onClick={() => handleNavigation('/admin')}
                  sx={{ 
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 2,
                    py: 1,
                    borderRadius: 1,
                    backgroundColor: isCurrentPath('/admin') ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  Admin
                </Button>
              )}
            </Box>
            
            {/* User area */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Role indicators */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                {user?.isAdmin && (
                  <Chip 
                    label="Admin" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#FF9900',
                      color: '#FFFFFF',
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }} 
                  />
                )}
                {isCurrentPenholder() && (
                  <Chip 
                    label="Penholder" 
                    size="small" 
                    sx={{ 
                      backgroundColor: '#007EB9',
                      color: '#FFFFFF',
                      fontWeight: 500,
                      fontSize: '0.75rem'
                    }} 
                  />
                )}
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#FFFFFF',
                    fontWeight: 500,
                    display: { xs: 'none', sm: 'block' }
                  }}
                >
                  {user?.displayName || user?.email}
                </Typography>
                
                <IconButton
                  size="large"
                  aria-label="account of current user"
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleMenu}
                  sx={{ 
                    color: '#FFFFFF',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    }
                  }}
                >
                  <AccountCircle />
                </IconButton>
              </Box>
            </Box>
            
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  border: '1px solid #CCCCCC',
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                }
              }}
            >
              <Box sx={{ px: 2, py: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Signed in as
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#252F3E' }}>
                  {user?.displayName || user?.email}
                </Typography>
                {/* Role information in menu */}
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                  {user?.isAdmin && (
                    <Chip label="Admin" size="small" color="secondary" />
                  )}
                  {isCurrentPenholder() && (
                    <Chip label="Penholder" size="small" color="info" />
                  )}
                </Box>
              </Box>
              <Divider />
              <MenuItem 
                onClick={handleSignOut}
                sx={{ 
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(37, 47, 62, 0.04)',
                  }
                }}
              >
                <ExitToApp sx={{ mr: 2, color: '#666666' }} />
                <Typography variant="body2">Sign Out</Typography>
              </MenuItem>
            </Menu>
          </Toolbar>
        </Box>
      </AppBar>
      
      {/* Main content area with AWS-style background */}
      <Box 
        component="main" 
        sx={{ 
          backgroundColor: '#F5F5F5',
          minHeight: 'calc(100vh - 64px)',
          py: 3
        }}
      >
        <Box sx={{ 
          mt: 1, 
          mb: 4, 
          px: 4,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%'
        }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
